import { Alert, Box } from '@mui/material';
import { useGridApiRef } from '@mui/x-data-grid-premium';
import { useCallback, useContext, useMemo } from 'react';
import { useSearchParams } from 'react-router';

import { UserContext } from '~/auth/userContext';
import { selectCarContracts } from '~/components/compliance/detailPage/carContracts';
import { ContractPreviewPanel } from '~/components/contractReview/ContractPreviewPanel';
import { getContractsTableColumns } from '~/components/contractReview/contractTableColumns';
import { mapContract } from '~/components/contractReview/mapContract';
import type { ContractReview } from '~/components/contractReview/types';
import { useTableRowPreview } from '~/components/rowPreview/useTableRowPreview';
import { LiftAITable } from '~/components/table';
import { useContractsForProperty } from '~/data/hooks/useContracts';
import type { API_Compliance } from '~/data/schemas/compliance';
import { useDetailTableHeight } from '~/hooks/useDetailTableHeight';
import { useLocaleTerms } from '~/hooks/useLocaleTerms';
import { canAccess, NAVIGATE_TO_CONTRACTS_PAGE } from '~/utils/role';
import { buildUrlForContract } from '~/utils/urlBuilder';

/**
 * The contracts covering this elevator: read from the property — a contract reaches a
 * car through its `propertyContracts` — and narrowed to the entries listing this car.
 * The request is the same one the tab strip's count makes, so opening this tab is a
 * cache hit.
 *
 * No governing contract here, unlike an invoice's version of this tab.
 * `activeContractId` is a fact about an invoice; a car is simply covered or it isn't.
 */
export function ElevatorContractsTab({ compliance }: { compliance: API_Compliance }) {
  const tableHeight = useDetailTableHeight();
  const apiRef = useGridApiRef();
  const { currencyCode, userDetails } = useContext(UserContext);
  const preview = useTableRowPreview({
    list: 'elevator_contracts',
    apiRef,
    // The contract page's own gate: the panel is the contract page in miniature.
    enabled: canAccess(userDetails.role, NAVIGATE_TO_CONTRACTS_PAGE),
  });
  const [searchParams] = useSearchParams();
  const terms = useLocaleTerms();
  const { contracts, isLoading } = useContractsForProperty(compliance.propertyId);

  const rows = useMemo<ContractReview[]>(
    () => selectCarContracts(contracts, compliance.propertyId, compliance.carId).map(mapContract),
    [contracts, compliance.propertyId, compliance.carId],
  );

  const columns = useMemo(
    () =>
      getContractsTableColumns<ContractReview>({
        currencyCode,
        vehiclesLabel: terms.vehicles,
      }).filter(
        // The property is fixed — this car's — so the properties roll-up says nothing, and a
        // contract-wide car count is the wrong number on a page about one unit.
        (column) => column.field !== 'properties' && column.field !== 'carCount',
      ),
    [currencyCode, terms.vehicles],
  );

  // A new tab rather than a navigation: the contract is opened to read what this unit is
  // owed under it.
  const rowHref = useCallback(
    (row: ContractReview) => buildUrlForContract(String(row.id), searchParams),
    [searchParams],
  );

  // An empty grid would say "No rows" where the reason matters.
  if (!isLoading && rows.length === 0) {
    return (
      <Box sx={{ p: 3, width: '100%' }}>
        <Alert severity="info">No contract covers this unit.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, width: '100%' }}>
      <LiftAITable<ContractReview>
        height={tableHeight}
        apiRef={apiRef}
        rows={rows}
        columns={columns}
        rowCount={rows.length}
        loading={isLoading}
        tableHeaderTitle="Contracts"
        rowHref={rowHref}
        toolbarProps={{ filterControls: preview.peekSwitch }}
        {...preview.tableProps}
      />
      {preview.enabled ? <ContractPreviewPanel rowPreview={preview.rowPreview} /> : null}
    </Box>
  );
}
